'use client';

import { Dialog } from '../../../components/dialog';
import { toasterApi } from '../../toast/ToastProvider';
import { useShallow } from 'zustand/shallow';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import {
  AddCredentialsDialogStep,
  type CredentialData,
  useAddCredentialsDialogStore,
} from './AddCredentialsDialogStore';
import { BasicInformationStep } from './steps/BasicInformationStep';
import { LoginInformationStep } from './steps/LoginInformationStep';
import { RequestAuthenticationStep } from './steps/RequestAuthenticationStep';
import { useValidateStep } from './utils';
import {
  CredentialDataSourcesProvider,
  type CredentialDataSources,
} from '../CredentialDataSourcesContext';

export type AddCredentialsDialogRole = {
  name: string;
  description?: string | null;
};

export type AddCredentialsDialogProps = {
  applicationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaveCredential: (credential: CredentialData) => void | Promise<void>;
  initialCredential?: CredentialData | null;
  hasGitIntegration?: boolean;
  customRoles?: AddCredentialsDialogRole[];
  dataSources?: CredentialDataSources;
};

const NO_DATA_SOURCES: CredentialDataSources = {};

export const AddCredentialsDialog = ({
  applicationId,
  open,
  onOpenChange,
  onSaveCredential,
  initialCredential,
  hasGitIntegration = false,
  customRoles,
  dataSources = NO_DATA_SOURCES,
}: AddCredentialsDialogProps) => {
  const t = useTranslations('designSystem.credentials.addDialog');
  const tDesignSystem = useTranslations('designSystem.applicationCredentials');
  const {
    currentStep,
    setCurrentStep,
    goToNextStep,
    goToPreviousStep,
    reset,
    initializeFromCredential,
    setValidationErrors,
    setFormMeta,
    credential,
    visitedSteps,
  } = useAddCredentialsDialogStore(
    useShallow((state) => ({
      currentStep: state.currentStep,
      setCurrentStep: state.setCurrentStep,
      goToNextStep: state.goToNextStep,
      goToPreviousStep: state.goToPreviousStep,
      reset: state.reset,
      initializeFromCredential: state.initializeFromCredential,
      setValidationErrors: state.setValidationErrors,
      setFormMeta: state.setFormMeta,
      credential: state.credential,
      visitedSteps: state.visitedSteps,
    })),
  );
  const { validateStep } = useValidateStep();
  const [isSaving, setIsSaving] = useState(false);
  const isLastStep =
    currentStep === AddCredentialsDialogStep.REQUEST_AUTHENTICATION;
  const isEditing = !!initialCredential;

  useEffect(() => {
    if (open && initialCredential) {
      const isEmail = initialCredential.username?.includes('@');
      const hasBasicAuth =
        !!initialCredential.basicAuthUsername ||
        !!initialCredential.basicAuthPassword;
      const hasMtlsCert = !!initialCredential.mtlsCert;
      const hasMtlsKey = !!initialCredential.mtlsKey;
      const hasMtlsPfx = !!initialCredential.mtlsPfx;
      const hasMtls = hasMtlsCert || hasMtlsKey || hasMtlsPfx;
      const mfaConfigs = (initialCredential.mfaConfig ?? []).flatMap((mfa) =>
        mfa.providerType
          ? [
              {
                id: mfa.id,
                enabled: mfa.enabled,
                providerType: mfa.providerType,
                totpSecret: mfa.totpSecret,
              },
            ]
          : [],
      );
      const mfaEnabled = mfaConfigs.some((mfa) => mfa.enabled);

      initializeFromCredential(initialCredential, {
        loginType: isEmail ? 'email' : 'username',
        email: isEmail ? initialCredential.username : '',
        requiresBasicAuth: hasBasicAuth,
        mfaEnabled,
        mfaConfigs,
        secretKeys:
          initialCredential.secrets?.length > 0
            ? initialCredential.secrets
            : [{ key: '', value: '' }],
        mtls: {
          enabled: hasMtls,
          certFileName: hasMtlsCert ? tDesignSystem('mtlsCertUploaded') : '',
          keyFileName: hasMtlsKey ? tDesignSystem('mtlsKeyUploaded') : '',
          pfxFileName: hasMtlsPfx ? tDesignSystem('mtlsCertUploaded') : '',
        },
        ...(initialCredential.bodySignature && {
          requestSigning: {
            headerName: initialCredential.bodySignature.headerName,
            signingSecret: initialCredential.bodySignature.secret,
            algorithm: initialCredential.bodySignature.algorithm,
          },
        }),
      });
    }

    if (!open) {
      reset();
    }
  }, [open, reset, initialCredential, initializeFromCredential, tDesignSystem]);

  useEffect(() => {
    if (!open || !customRoles) return;
    setFormMeta({
      customRoles: customRoles.map((role) => ({
        name: role.name,
        description: role.description ?? '',
      })),
    });
  }, [open, customRoles, setFormMeta]);

  if (!applicationId) {
    return null;
  }

  const steps = [
    { label: t('steps.basicInformation.title') },
    { label: t('steps.loginInformation.title') },
    { label: t('steps.requestAuthentication.title') },
  ];
  const stepBody =
    currentStep === AddCredentialsDialogStep.BASIC_INFORMATION ? (
      <BasicInformationStep
        applicationId={applicationId}
        hasGitIntegration={hasGitIntegration}
        isEditing={isEditing}
      />
    ) : currentStep === AddCredentialsDialogStep.LOGIN_INFORMATION ? (
      <LoginInformationStep isEditing={isEditing} />
    ) : currentStep === AddCredentialsDialogStep.REQUEST_AUTHENTICATION ? (
      <RequestAuthenticationStep />
    ) : null;
  const handleNextStep = async () => {
    const errors = validateStep(currentStep);

    if (Object.values(errors).some(Boolean)) {
      setValidationErrors(errors);

      if (errors.totpSecret) {
        toasterApi.error({ title: errors.totpSecret });

        if (currentStep !== AddCredentialsDialogStep.BASIC_INFORMATION) {
          setCurrentStep(AddCredentialsDialogStep.BASIC_INFORMATION);
        }

        return;
      }

      if (errors.authentication) {
        toasterApi.error({ title: errors.authentication });
      }

      return;
    }

    if (isLastStep) {
      setIsSaving(true);

      try {
        await onSaveCredential(credential);
      } finally {
        setIsSaving(false);
      }

      return;
    }

    goToNextStep();
  };

  const handlePreviousStep = () => {
    if (currentStep === AddCredentialsDialogStep.BASIC_INFORMATION) {
      onOpenChange(false);
    } else {
      goToPreviousStep();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={isEditing ? t('editTitle') : t('title')}
      description={t('description')}
      closeLabel={t('closeDialogAriaLabel')}
      stepper={{
        steps,
        activeStepIndex: currentStep,
        isCurrent: (stepIndex) => stepIndex === currentStep,
        isCompleted: (stepIndex) =>
          stepIndex !== currentStep && (isEditing || stepIndex < currentStep),
        isDisabled: (stepIndex) =>
          stepIndex !== currentStep && !visitedSteps.has(stepIndex),
        onStepClick: (stepIndex) => setCurrentStep(stepIndex),
      }}
      secondaryAction={{
        label:
          currentStep === AddCredentialsDialogStep.BASIC_INFORMATION
            ? t('cancel')
            : t('back'),
        onClick: handlePreviousStep,
      }}
      primaryAction={{
        label: isLastStep ? (isEditing ? t('save') : t('add')) : t('next'),
        onClick: handleNextStep,
        disabled: isSaving,
        loading: isSaving,
      }}
    >
      <CredentialDataSourcesProvider dataSources={dataSources}>
        {stepBody}
      </CredentialDataSourcesProvider>
    </Dialog>
  );
};
